import { RatePrice } from "@/components/live/RatePrice";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, MapPin, SlidersHorizontal, Grid3x3, List, Map,
  Bot, RefreshCw, ArrowLeft, Loader2, ChevronDown, Radio, Heart, Bookmark, Play, Pause, Trash2 } from "lucide-react";
import { api, API } from "@/lib/api";
import AppIcon, { canonicalIconName } from "@/components/AppIcon";
import { Input } from "@/components/ui/input";
import LiveWorldMap from "./LiveWorldMap";
import LiveWorldAinaSheet from "./LiveWorldAinaSheet";
import PostAsNeedSheet from "./PostAsNeedSheet";
import UrgencyBadge from "@/components/UrgencyBadge";
import { toast } from "sonner";

/**
 * LiveWorld — Phase-1 mobile-first realtime feed.
 *
 * Shape: <header> · <filter bar> · <view switcher> · <category strip> ·
 *        <grid | list | map> · <floating AINA>.
 *
 * All data comes from /api/live-world/feed (mixed rows). This page does not
 * touch Home / Account / Messages / Profile / the existing bottom nav — a
 * single floating pill on Home is the entry point.
 */
const KINDS = [
  { id: "all",       label: "All",       color: "bg-stone-800"    },
  { id: "needs",     label: "Needs",     color: "bg-red-500"      },
  { id: "offers",    label: "Offers",    color: "bg-emerald-600"  },
  { id: "auctions",  label: "Auctions",  color: "bg-blue-600"     },
  { id: "leads",     label: "Leads",     color: "bg-amber-500"    },
  { id: "matches",   label: "Matches",   color: "bg-purple-600"   },
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "WFC"];
const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED", WFC: "WFC" };

const STATUS_BADGE = {
  live:    { bg: "bg-emerald-500",  label: "🟢 LIVE"    },
  need:    { bg: "bg-red-500",      label: "🔴 NEED"    },
  offer:   { bg: "bg-emerald-600",  label: "🟢 OFFER"   },
  auction: { bg: "bg-blue-600",     label: "🔵 AUCTION" },
  lead:    { bg: "bg-amber-500",    label: "🟠 LEAD"    },
  matched: { bg: "bg-purple-600",   label: "🟣 MATCHED" },
  stale:   { bg: "bg-stone-400",    label: "⚪ STALE"    },
  delayed: { bg: "bg-amber-600",    label: "🟡 DELAYED"  },
  unavailable: { bg: "bg-stone-500", label: "⚪ UNAVAILABLE" },
};

export default function LiveWorld() {
  const nav = useNavigate();
  const [kind, setKind] = useState("all");
  const [category, setCategory] = useState(null);
  const [view, setView] = useState("grid");        // grid | list | map
  const [currency, setCurrency] = useState("INR");
  const [sp] = useSearchParams();
  const [q, setQ] = useState(() => sp.get("q") || "");
  const [loc, setLoc] = useState(null);             // { lat, lng, label }
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showAina, setShowAina] = useState(false);
  const [selected, setSelected] = useState(null);   // map-pin bottom card
  const [postNeedFor, setPostNeedFor] = useState(null);   // P4 · buyer intent sheet
  const [priceMax, setPriceMax] = useState(null);
  const [priceMin, setPriceMin] = useState(null);
  const [radiusKm, setRadiusKm] = useState(20);
  const [endingWithinMin, setEndingWithinMin] = useState(null);
  const [sseState, setSseState] = useState("idle"); // idle | live | fallback
  const [showSaved, setShowSaved] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const sseSeqRef = useRef(null);

  const loadSaved = useCallback(async () => {
    try {
      const r = await api.get("/live-world/saved-searches");
      setSavedList(r.data.items || []);
    } catch { /* anonymous — silent */ }
  }, []);

  const saveCurrentSearch = async () => {
    const name = window.prompt(
      "Name this search",
      (q || category || kind || "My search").toString().slice(0, 40),
    );
    if (!name) return;
    const filters = { kind, category, currency, q, priceMax, priceMin, radiusKm };
    try {
      await api.post("/live-world/saved-searches", { name, filters, text: q });
      toast.success(`Saved: ${name}`);
      loadSaved();
    } catch (e) {
      toast.error(e?.response?.status === 401
        ? "Log in to save searches."
        : "Couldn't save search.");
    }
  };

  const applySavedSearch = (item) => {
    const f = item.filters || {};
    if (f.kind) setKind(f.kind);
    setCategory(f.category ?? null);
    if (f.currency) setCurrency(f.currency);
    setQ(f.q || "");
    setPriceMax(f.priceMax ?? null);
    setPriceMin(f.priceMin ?? null);
    setRadiusKm(f.radiusKm || 20);
    setShowSaved(false);
    toast.success(`Applied: ${item.name}`);
  };

  const patchSaved = async (id, patch) => {
    try {
      await api.patch(`/live-world/saved-searches/${id}`, patch);
      loadSaved();
    } catch { /* silent */ }
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/live-world/saved-searches/${id}`);
      loadSaved();
    } catch { /* silent */ }
  };

  // detect user location (once, best-effort; falls back to Mumbai on refuse)
  useEffect(() => {
    if (!navigator.geolocation) { setLoc({ lat: 19.076, lng: 72.877, label: "Mumbai" }); return; }
    navigator.geolocation.getCurrentPosition(
      p => setLoc({ lat: p.coords.latitude, lng: p.coords.longitude, label: "Near me" }),
      () => setLoc({ lat: 19.076, lng: 72.877, label: "Mumbai" }),
      { timeout: 4000, maximumAge: 60_000 },
    );
  }, []);

  // categories (one-shot)
  useEffect(() => {
    api.get("/live-world/categories").then(r => setCats(r.data.categories || [])).catch(() => {});
  }, []);

  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    if (!loc) return;
    const mine = ++reqSeq.current;  // a slower, older response must never overwrite newer filters
    setBusy(true);
    try {
      const params = new URLSearchParams({
        kind, currency, lat: loc.lat, lng: loc.lng, radius_km: radiusKm, limit: 60,
      });
      if (category) params.append("category", category);
      if (q.trim()) params.append("q", q.trim());
      if (priceMax != null) params.append("price_max", priceMax);
      if (priceMin != null) params.append("price_min", priceMin);
      if (endingWithinMin != null) params.append("ending_within_min", endingWithinMin);
      const r = await api.get(`/live-world/feed?${params}`);
      if (mine === reqSeq.current) setRows(r.data.rows || []);
    } catch (e) {
      if (mine === reqSeq.current) setRows([]);
    } finally { if (mine === reqSeq.current) setBusy(false); }
  }, [kind, category, currency, q, loc, radiusKm, priceMax, priceMin, endingWithinMin]);
  useEffect(() => { load(); }, [load]);
  // SSE/polling must always call the CURRENT load (with the selected category/filters), not a stale closure.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // SSE realtime — falls back to polling when disconnected
  useEffect(() => {
    if (!loc) return undefined;
    let es;
    let pollTimer;
    let closed = false;

    const startPolling = () => {
      setSseState("fallback");
      if (pollTimer) return;
      pollTimer = setInterval(() => loadRef.current(), 30_000);
    };
    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const connectSse = () => {
      try {
        const seq = sseSeqRef.current;
        const url = `${API}/live-world/stream${seq ? `?since=${encodeURIComponent(seq)}` : ""}`;
        es = new EventSource(url, { withCredentials: true });
        es.addEventListener("ready", (ev) => {
          setSseState("live");
          stopPolling();
          try {
            const d = JSON.parse(ev.data);
            if (d.seq) sseSeqRef.current = d.seq;
          } catch {}
        });
        es.addEventListener("update", (ev) => {
          try {
            const d = JSON.parse(ev.data);
            if (d.seq) sseSeqRef.current = d.seq;
            // Any event → refetch the feed with the current filters
            loadRef.current();
          } catch {}
        });
        es.onerror = () => {
          if (closed) return;
          try { es.close(); } catch {}
          es = null;
          startPolling();
          // Try to reconnect after a short delay
          setTimeout(() => { if (!closed) connectSse(); }, 5000);
        };
      } catch {
        startPolling();
      }
    };

    connectSse();
    return () => {
      closed = true;
      if (es) { try { es.close(); } catch {} }
      stopPolling();
    };
  }, [loc, load]);

  const handleAinaApply = (parsed) => {
    const f = parsed?.filters || {};
    if (f.kind) setKind(f.kind);
    if (f.category !== undefined) setCategory(f.category || null);
    if (f.price_max != null) setPriceMax(f.price_max); else setPriceMax(null);
    if (f.price_min != null) setPriceMin(f.price_min); else setPriceMin(null);
    if (f.radius_km) setRadiusKm(f.radius_km);
    if (f.product) setQ(f.product);
  };

  const openRow = (row) => {
    if (row.kind === "auction") { nav(`/live-world/auction/${row.id}`); return; }
    setSelected(row);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-24" data-testid="live-world">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-3 pt-3 pb-2 flex items-center gap-2">
          <button onClick={() => nav("/")} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="lw-back">
            <ArrowLeft size={18}/>
          </button>
          <div className="flex items-center gap-1.5 font-black text-stone-900">
            <span className="text-lg">🌍</span> LIVE WORLD
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => { setShowSaved(true); loadSaved(); }}
                    className="p-2 rounded-full hover:bg-amber-50 text-stone-500 hover:text-amber-600"
                    title="Saved searches"
                    data-testid="lw-saved-open">
              <Bookmark size={16}/>
            </button>
            <button onClick={saveCurrentSearch}
                    className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black bg-amber-50 text-amber-800 hover:bg-amber-100"
                    title="Save this search"
                    data-testid="lw-save-search">
              SAVE
            </button>
            <button onClick={() => nav("/live-world/watchlist")}
                    className="p-2 rounded-full hover:bg-rose-50 text-stone-500 hover:text-rose-500"
                    title="My Watchlist"
                    data-testid="lw-watchlist-open">
              <Heart size={16}/>
            </button>
            <button onClick={load} disabled={busy} className="p-2 rounded-full hover:bg-stone-100 disabled:opacity-40" data-testid="lw-refresh">
              {busy ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
            </button>
            <button onClick={() => setShowFilter(true)} className="p-2 rounded-full hover:bg-stone-100" data-testid="lw-filter-open">
              <SlidersHorizontal size={16}/>
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-3 pb-2 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-1.5 bg-stone-100 rounded-full px-3 py-1.5">
            <Search size={14} className="text-stone-500 shrink-0"/>
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="e.g. 200 kg iron under ₹55"
              className="border-none bg-transparent focus-visible:ring-0 text-sm h-6 px-0"
              data-testid="lw-search"
              onKeyDown={e => e.key === "Enter" && load()}
            />
          </div>
          <Link to={`/search?q=${encodeURIComponent(q)}`} className="text-[11px] font-bold text-sky-700 underline whitespace-nowrap" data-testid="lw-search-everywhere">Search all</Link>
          <button className="inline-flex items-center gap-1 text-xs text-stone-600 font-bold" data-testid="lw-location">
            <MapPin size={13} className="text-emerald-600"/> {loc?.label || "…"}
          </button>
          <div className="relative">
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              className="appearance-none bg-emerald-50 border border-emerald-200 rounded-full px-2 pr-6 py-1 text-xs font-black text-emerald-800"
              data-testid="lw-currency"
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-emerald-700 pointer-events-none"/>
          </div>
        </div>
        {/* Filter bar */}
        <div className="max-w-3xl mx-auto px-3 pb-2 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-1.5 min-w-min">
            {KINDS.map(k => (
              <button
                key={k.id}
                onClick={() => setKind(k.id)}
                className={`shrink-0 px-3 py-1 rounded-full text-xs font-black transition-all ${
                  kind === k.id ? `${k.color} text-white shadow` : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                }`}
                data-testid={`lw-kind-${k.id}`}
              >{k.label}</button>
            ))}
            <EndingSoonChip
              active={endingWithinMin != null}
              endingWithinMin={endingWithinMin}
              onToggle={(mins) => {
                setEndingWithinMin((cur) => cur != null ? null : mins);
                if (kind !== "auctions") setKind("auctions");
              }}
            />
          </div>
        </div>
        {/* View switcher */}
        <div className="max-w-3xl mx-auto px-3 pb-2 flex items-center justify-between">
          <div className="inline-flex items-center gap-0.5 bg-stone-100 rounded-full p-0.5">
            {[["grid", Grid3x3], ["list", List], ["map", Map]].map(([v, Ic]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 text-[11px] font-bold transition-all ${
                  view === v ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
                }`}
                data-testid={`lw-view-${v}`}
              >
                <Ic size={12}/> {v.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-stone-500 font-bold">{rows.length} result{rows.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {/* Category strip */}
      <div className="max-w-3xl mx-auto px-3 py-3 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 min-w-min">
          <button
            onClick={() => setCategory(null)}
            className={`shrink-0 flex flex-col items-center gap-0.5 px-2 py-1 rounded-2xl transition-all ${
              !category ? "bg-emerald-50 ring-2 ring-emerald-500" : "hover:bg-stone-100"
            }`}
            data-testid="lw-cat-all"
          >
            <div className="w-11 h-11 rounded-2xl bg-stone-100 grid place-items-center text-lg">🌍</div>
            <div className="text-[10px] font-bold text-stone-700">All</div>
          </button>
          {cats.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(category === c.id ? null : c.id)}
              className={`shrink-0 flex flex-col items-center gap-0.5 px-2 py-1 rounded-2xl transition-all ${
                category === c.id ? "bg-emerald-50 ring-2 ring-emerald-500" : "hover:bg-stone-100"
              }`}
              data-testid={`lw-cat-${c.id}`}
            >
              <div className="w-11 h-11 rounded-2xl grid place-items-center">
                <AppIcon name={c.icon} size={40}
                         fallback={<div className="w-8 h-8 rounded-full bg-stone-200"/>}/>
              </div>
              <div className="text-[10px] font-bold text-stone-700 max-w-[52px] truncate">{c.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Live view */}
      <div className="max-w-3xl mx-auto px-3">
        {busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500"><Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading Live World…</div>
        )}
        {!busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="lw-empty">
            {endingWithinMin != null
              ? "No auctions ending within this window right now. Tap the chip again to clear it, or widen the filter."
              : "No live activity here yet. Try a different category or filter."}
          </div>
        )}
        {view === "grid" && (
          <div className="grid grid-cols-2 gap-2.5" data-testid="lw-grid">
            {rows.map(r => <LiveCard key={r.id} row={r} currency={currency} onClick={() => openRow(r)}/>)}
          </div>
        )}
        {view === "list" && (
          <div className="space-y-2" data-testid="lw-list">
            {rows.map(r => <LiveListRow key={r.id} row={r} currency={currency} onClick={() => openRow(r)}/>)}
          </div>
        )}
        {view === "map" && (
          <LiveWorldMap rows={rows} loc={loc} onSelect={openRow} height="calc(100vh - 22rem)"/>
        )}
      </div>

      {/* Floating AINA */}
      <button
        onClick={() => setShowAina(true)}
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E05E36] to-[#c94e2a] shadow-lg grid place-items-center text-white hover:scale-105 transition-transform"
        data-testid="lw-aina"
        title="Ask AINA to filter"
      >
        <Bot size={22}/>
      </button>

      {/* SSE state pill */}
      <div className="fixed bottom-24 left-4 z-40" data-testid="lw-sse-state">
        <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black shadow ${
          sseState === "live" ? "bg-emerald-500 text-white" :
          sseState === "fallback" ? "bg-amber-500 text-white" : "bg-stone-300 text-stone-700"
        }`}>
          <Radio size={11} className={sseState === "live" ? "animate-pulse" : ""}/>
          {sseState === "live" ? "REALTIME" : sseState === "fallback" ? "POLLING" : "…"}
        </div>
      </div>

      <LiveWorldAinaSheet
        open={showAina}
        onClose={() => setShowAina(false)}
        loc={loc}
        currency={currency}
        onApply={handleAinaApply}
      />

      <PostAsNeedSheet
        open={!!postNeedFor}
        need={postNeedFor}
        loc={loc}
        onClose={() => setPostNeedFor(null)}
        onCreated={() => { /* stays on Live World; toast fired inside sheet */ }}
      />

      {/* Selected pin bottom card (map tap) */}
      {selected && (
        <div className="fixed inset-x-0 bottom-16 z-[60] px-3" onClick={() => setSelected(null)} data-testid="lw-pin-card">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-stone-200 shadow-2xl p-3 flex items-center gap-3"
               onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 rounded-xl bg-stone-50 grid place-items-center overflow-hidden shrink-0">
              {selected.image
                ? <img src={selected.image} alt="" className="w-full h-full object-cover"/>
                : <AppIcon name={selected.category} size={52} fallback={<div className="text-2xl">{iconFallback(selected.category)}</div>}/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase font-black" style={{ color: (STATUS_BADGE[selected.status] || STATUS_BADGE.live).bg.includes("bg-") ? undefined : undefined }}>
                <span className={`inline-block px-1.5 py-0.5 rounded-full text-white ${(STATUS_BADGE[selected.status] || STATUS_BADGE.live).bg}`}>
                  {(STATUS_BADGE[selected.status] || STATUS_BADGE.live).label}
                </span>
              </div>
              <div className="text-sm font-black text-stone-900 truncate mt-1">{selected.title}</div>
              {selected.kind === "rate" && <RatePrice row={selected} detailed />}
              <div className="text-xs font-bold text-emerald-700">
                {selected.ask != null && `ASK ₹${Number(selected.ask).toLocaleString("en-IN")}`}
                {selected.ask == null && selected.bid != null && `BID ₹${Number(selected.bid).toLocaleString("en-IN")}`}
                {selected.distance_km != null && <span className="ml-2 text-stone-500">📍 {selected.distance_km} km</span>}
              </div>
            </div>
            {selected.kind === "auction" ? (
              <button onClick={() => nav(`/live-world/auction/${selected.id}`)}
                      className="px-3 py-2 rounded-full bg-[#E05E36] text-white text-xs font-black">
                BID →
              </button>
            ) : selected.kind === "need" ? (
              <button
                data-testid="lw-post-as-need-btn"
                onClick={() => { setPostNeedFor(selected); setSelected(null); }}
                className="px-3 py-2 rounded-full bg-red-500 text-white text-xs font-black">
                POST AS NEED →
              </button>
            ) : (
              <button onClick={() => setSelected(null)}
                      className="px-3 py-2 rounded-full bg-stone-100 text-stone-700 text-xs font-black">
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter bottom sheet */}
      {showFilter && (
        <div className="fixed inset-0 bg-black/50" style={{ zIndex: "var(--z-modal)" }} onClick={() => setShowFilter(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-stone-300 rounded-full mx-auto"/>
            <div className="font-black text-stone-900">Filters</div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1.5">Kind</div>
              <div className="flex flex-wrap gap-1.5">{KINDS.map(k => (
                <button key={k.id} onClick={() => setKind(k.id)} className={`px-3 py-1 rounded-full text-xs font-bold ${kind === k.id ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}>{k.label}</button>
              ))}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1.5">Category</div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setCategory(null)} className={`px-3 py-1 rounded-full text-xs font-bold ${!category ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}>All</button>
                {cats.map(c => (
                  <button key={c.id} onClick={() => setCategory(c.id)} className={`px-3 py-1 rounded-full text-xs font-bold ${category === c.id ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}>{c.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1.5">Max price (₹)</div>
                <input type="number" value={priceMax ?? ""} onChange={(e) => setPriceMax(e.target.value ? Number(e.target.value) : null)}
                       placeholder="no cap"
                       className="w-full bg-stone-100 rounded-full px-3 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                       data-testid="lw-filter-price-max"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1.5">Radius (km)</div>
                <input type="range" min={1} max={100} value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))}
                       className="w-full"
                       data-testid="lw-filter-radius"/>
                <div className="text-[10px] text-stone-500 font-bold">{radiusKm} km</div>
              </div>
            </div>
            <button onClick={saveCurrentSearch}
                    className="w-full py-2 rounded-full bg-amber-100 text-amber-800 font-black text-xs hover:bg-amber-200"
                    data-testid="lw-filter-save-search">
              💾 Save current search
            </button>
            <button onClick={() => setShowFilter(false)} className="w-full py-2.5 rounded-full bg-emerald-600 text-white font-black" data-testid="lw-filter-apply">Show {rows.length} results</button>
          </div>
        </div>
      )}

      {/* Saved searches bottom sheet */}
      {showSaved && (
        <div className="fixed inset-0 bg-black/50" style={{ zIndex: "var(--z-modal)" }} onClick={() => setShowSaved(false)} data-testid="lw-saved-sheet">
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-stone-300 rounded-full mx-auto"/>
            <div className="flex items-center gap-2">
              <Bookmark size={16} className="text-amber-600"/>
              <div className="font-black text-stone-900">Saved searches</div>
              <button onClick={saveCurrentSearch}
                      className="ml-auto px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-black text-xs"
                      data-testid="lw-saved-add">
                + Save current
              </button>
            </div>
            {savedList.length === 0 && (
              <div className="py-8 text-center text-xs text-stone-500" data-testid="lw-saved-empty">
                Nothing saved yet. Set some filters, then tap "Save current".
              </div>
            )}
            <div className="space-y-2">
              {savedList.map((it) => (
                <div key={it.search_id} className="bg-stone-50 border border-stone-200 rounded-2xl p-3 flex items-center gap-2"
                     data-testid={`lw-saved-item-${it.search_id}`}>
                  <button onClick={() => applySavedSearch(it)} className="flex-1 min-w-0 text-left">
                    <div className="font-black text-stone-900 text-sm truncate">{it.name}</div>
                    <div className="text-[10px] text-stone-500 font-bold truncate">
                      {[it.filters?.kind, it.filters?.category, it.filters?.q,
                        it.filters?.priceMax ? `≤₹${it.filters.priceMax}` : null,
                        it.filters?.radiusKm ? `${it.filters.radiusKm}km` : null]
                        .filter(Boolean).join(" · ")}
                      {it.paused && <span className="ml-1 text-amber-600">· PAUSED</span>}
                    </div>
                  </button>
                  <button onClick={() => patchSaved(it.search_id, { paused: !it.paused })}
                          className="p-1.5 rounded-full hover:bg-stone-200 text-stone-600"
                          title={it.paused ? "Resume" : "Pause"}>
                    {it.paused ? <Play size={14}/> : <Pause size={14}/>}
                  </button>
                  <button onClick={() => deleteSaved(it.search_id)}
                          className="p-1.5 rounded-full hover:bg-rose-50 text-rose-500"
                          title="Delete">
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LiveCard — the dense grid card
// ─────────────────────────────────────────────────────────────────────────────
function LiveCard({ row, currency, onClick }) {
  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.live;
  const priceCurrency = row.conv_currency || row.currency || "INR";
  const bidValue = row.bid_conv ?? row.bid;
  const askValue = row.ask_conv ?? row.ask;
  const sym = CURRENCY_SYMBOL[priceCurrency] || priceCurrency;
  const isAuction = row.kind === "auction";
  return (
    <div className="relative" data-testid={`lw-card-${row.id}`}>
      {isAuction && <WatchToggle auctionId={row.id} className="absolute top-3 right-3 z-10"/>}
      {isAuction && (row.urgency === "ending_soon" || row.urgency === "ending_very_soon") && (
        <div className="absolute top-3 left-3 z-10">
          <UrgencyBadge urgency={row.urgency} closes_at={row.meta?.closes_at} compact/>
        </div>
      )}
      <div role="button" tabIndex={0}
           onClick={onClick}
           onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
           className="text-left bg-white border border-stone-200 rounded-2xl p-2.5 hover:shadow-md transition-shadow cursor-pointer block">
      <div className="relative w-full aspect-square rounded-xl bg-stone-50 grid place-items-center overflow-hidden mb-2">
        {row.image ? (
          <img src={row.image} alt="" className="w-full h-full object-cover" loading="lazy"/>
        ) : (
          <AppIcon name={row.category} size={80}
                   fallback={<div className="text-4xl">{iconFallback(row.category)}</div>}/>
        )}
        <div className={`absolute top-1.5 left-1.5 ${badge.bg} text-white text-[9px] font-black px-1.5 py-0.5 rounded-full`}>
          {badge.label}
        </div>
      </div>
      <div className="font-black text-stone-900 text-xs truncate">{row.title}</div>
      {row.unit && row.kind !== "rate" && <div className="text-[10px] text-stone-500">{row.unit}</div>}
      <div className="mt-1.5 space-y-0.5">
        {row.kind === "rate" && <RatePrice row={row} />}
        {bidValue != null && <div className="text-[11px] font-bold text-red-600">BID {sym}{formatMoney(bidValue)}</div>}
        {askValue != null && <div className="text-[11px] font-bold text-emerald-700">ASK {sym}{formatMoney(askValue)}</div>}
        {row.kind === "auction" && row.meta?.bids != null && (
          <div className="text-[10px] text-blue-700 font-bold">{row.meta.bids} bids</div>
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] text-stone-500">
        {row.distance_km != null && <div>📍 {row.distance_km} km</div>}
        {row.delta_pct != null && (
          <div className={row.delta_pct >= 0 ? "text-emerald-600" : "text-red-600"}>
            {row.delta_pct >= 0 ? "▲" : "▼"} {Math.abs(row.delta_pct)}%
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function LiveListRow({ row, currency, onClick }) {
  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.live;
  const priceCurrency = row.conv_currency || row.currency || "INR";
  const bidValue = row.bid_conv ?? row.bid;
  const askValue = row.ask_conv ?? row.ask;
  const sym = CURRENCY_SYMBOL[priceCurrency] || priceCurrency;
  const isAuction = row.kind === "auction";
  return (
    <div className="relative" data-testid={`lw-row-${row.id}`}>
      {isAuction && <WatchToggle auctionId={row.id} className="absolute top-2 right-2 z-10"/>}
      <div role="button" tabIndex={0}
           onClick={onClick}
           onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
           className="w-full text-left bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer">
      <div className="w-14 h-14 rounded-xl bg-stone-50 grid place-items-center overflow-hidden shrink-0">
        {row.image ? <img src={row.image} alt="" className="w-full h-full object-cover"/> :
         <AppIcon name={row.category} size={48} fallback={<div className="text-2xl">{iconFallback(row.category)}</div>}/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className={`${badge.bg} text-white text-[9px] font-black px-1.5 py-0.5 rounded-full`}>{badge.label}</div>
          {row.distance_km != null && <div className="text-[10px] text-stone-500 font-bold">📍 {row.distance_km} km</div>}
        </div>
        <div className="font-black text-stone-900 text-sm truncate">{row.title}</div>
        {row.kind === "rate" && <RatePrice row={row} />}
        <div className="flex items-center gap-3 text-xs mt-0.5">
          {bidValue != null && <div className="font-bold text-red-600">BID {sym}{formatMoney(bidValue)}{row.unit || ""}</div>}
          {askValue != null && <div className="font-bold text-emerald-700">ASK {sym}{formatMoney(askValue)}{row.unit || ""}</div>}
        </div>
      </div>
      </div>
    </div>
  );
}

function formatMoney(v) {
  if (v == null) return "";
  if (Math.abs(v) >= 100_000) return v.toLocaleString("en-IN");
  return String(v);
}

function iconFallback(cat) {
  const m = { gold: "🥇", silver: "🥈", currency: "💰", commodity: "🌾", crypto: "₿",
              vehicle: "🚗", property: "🏠", auction: "🔨", transport: "🚚",
              jobs: "💼", education: "🎓", services: "🛠", podcaster: "🎙",
              wfc: "🌍" };
  return m[cat] || "•";
}

/** "🔨 Ending in Xh" — one-tap urgency lens on the kind bar. Reads the
 * `ending_soon_min` threshold from the central urgency-config so we
 * never hardcode 60 minutes in the frontend. */
function EndingSoonChip({ active, endingWithinMin, onToggle }) {
  const [minutes, setMinutes] = React.useState(60);
  React.useEffect(() => {
    let cancel = false;
    api.get("/live-world/urgency-config")
      .then((r) => {
        if (cancel) return;
        const t = r.data?.thresholds?.ending_soon_min;
        if (typeof t === "number" && t > 0) setMinutes(t);
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  const label = minutes >= 60
    ? `Ending in ${Math.round(minutes / 60)}h`
    : `Ending in ${minutes}m`;
  return (
    <button
      onClick={() => onToggle(minutes)}
      className={`shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black transition-all ${
        active
          ? "bg-rose-600 text-white shadow"
          : "bg-stone-100 text-stone-700 hover:bg-rose-50 hover:text-rose-700"
      }`}
      title={active
        ? `Showing auctions ending within ${minutes} minutes — tap to clear`
        : `Show only auctions ending within ${minutes} minutes`}
      data-testid="lw-ending-soon-chip"
    >
      <span aria-hidden="true">🔨</span> {label}
    </button>
  );
}

/** Compact ♡ toggle used on auction cards. Client-side state; server-side
 * persistence via /api/live-world/auctions/{id}/watch. Anonymous users see
 * the same button — they get an auth prompt when they tap. */
function WatchToggle({ auctionId, className = "" }) {  const [watching, setWatching] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    let ignore = false;
    api.get(`/live-world/auctions/${auctionId}/watch`)
      .then((r) => { if (!ignore) setWatching(!!r.data?.watching); })
      .catch(() => {});
    return () => { ignore = true; };
  }, [auctionId]);
  const toggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const prev = watching;
    setWatching(!prev);
    try {
      if (prev) {
        await api.delete(`/live-world/auctions/${auctionId}/watch`);
      } else {
        await api.post(`/live-world/auctions/${auctionId}/watch`);
      }
    } catch (err) {
      setWatching(prev);
      // Fail silently; the user sees the heart revert
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`p-1.5 rounded-full bg-white/95 backdrop-blur shadow border border-stone-200 transition-colors ${
        watching ? "text-rose-500" : "text-stone-400 hover:text-rose-500"
      } ${className}`}
      title={watching ? "Unwatch" : "Watch auction"}
      data-testid={`lw-watch-toggle-${auctionId}`}
    >
      <Heart size={13} className={watching ? "fill-rose-500" : ""}/>
    </button>
  );
}
